import type { ExitSlipExitSlipsBySkillsQuery$data } from '../../graphQLOperations/__generated__/ExitSlipExitSlipsBySkillsQuery.graphql';
import type { SkillExitSlipProgress, SkillExitSlipTicket } from '../../models/ExitSlip';
import { EXIT_SLIP_PASSING_RATIO, type PendingExitSlipFinish } from './exitSlipOutcome';

/**
 * Maps the studentExitSlipReport rows (exitSlipsBySkills, scoped to one student and one target
 * group) onto the SkillExitSlipProgress the unit-path cards' badges read out of redux.
 *
 * This is the resolver the badge was built against and mocked for: the report is the only source that
 * answers how many exit tickets exist for a skill, which is the denominator of the ON FIRE rule (see
 * isSkillOnFire). It replaces exitSlipSkillBadgeMocks entirely.
 */

type ReportRows = ExitSlipExitSlipsBySkillsQuery$data['studentExitSlipReport'];
export type SkillExitSlipReportRow = ReportRows[number];
type ReportLesson = SkillExitSlipReportRow['curriculumLessons'][number];
type ReportSession = ReportLesson['classExitSlips'][number]['learnosityExitSlipSession'];

/**
 * The report speaks in raw ints (`BaseSkillInfo.id: Int!`, reaching us as `skillId` — see the alias
 * note on the query), while everything the badge is matched against — StudentSkillStates.skill.id,
 * the keys of exitSlipSkillProgressSlice — carries the global id form. Converting here rather than
 * at the lookup keeps the slice's contract unchanged and means a card never has to know the report
 * exists.
 */
export const toSkillNodeId = (skillId: number): string => `SkillNode:${skillId}`;

/**
 * Same conversion for an exit ticket's own id. Matters beyond telling two tickets apart: it is what
 * lets a ticket from this report be recognised as the one due today, whose id reaches the dashboard
 * through the other query in the global form (see summarizeStudentExitSlips).
 */
export const toClassExitSlipNodeId = (classExitSlipId: number): string => `ClassExitSlipNode:${classExitSlipId}`;

/**
 * Points earned over points available for one session, the same 0..1 ratio the collapsed banner and
 * the badge's hover panel show. A session with no points available scores 0 rather than NaN — that
 * would otherwise reach the panel's percentage and render "NaN%".
 */
export const sessionScoreRatio = (session: NonNullable<ReportSession>): number =>
  session.maxScore === 0 ? 0 : session.score / session.maxScore;

const latestAssignment = (lesson: ReportLesson) => lesson.classExitSlips[lesson.classExitSlips.length - 1] ?? null;

const assignmentTicket = (
  lesson: ReportLesson,
  classExitSlip: ReportLesson['classExitSlips'][number],
  pendingFinish: PendingExitSlipFinish | null,
): SkillExitSlipTicket | null => {
  const id = toClassExitSlipNodeId(classExitSlip.classExitSlipId);
  // No session means the student has not sat it. This is the only signal available — the report's
  // session type carries no `isComplete`, and the underlying flag is always true anyway, so its
  // absence is what says "not taken".
  const session = classExitSlip.learnosityExitSlipSession;
  if (session) {
    return { id, lessonNumber: lesson.number, scoreRatio: sessionScoreRatio(session) };
  }

  // Sat on this device a moment ago, and the report — fetched once when the dashboard opened — has
  // no way of knowing yet. Counting it here is what moves the card's badge the instant the student
  // finishes, the same optimistic step the unit header's badge takes. The record is dropped once the
  // server reports the session, at which point the branch above answers instead.
  if (pendingFinish?.exitSlipId === id) {
    return { id, lessonNumber: lesson.number, scoreRatio: pendingFinish.scoreRatio };
  }

  return null;
};

const lessonTicket = (
  lesson: ReportLesson,
  pendingFinish: PendingExitSlipFinish | null,
): SkillExitSlipTicket | null => {
  const assignment = latestAssignment(lesson);
  return assignment ? assignmentTicket(lesson, assignment, pendingFinish) : null;
};

/**
 * Curriculum order for two lesson numbers.
 *
 * Lesson numbers are dotted decimals ("3.4", "13.12") and have to be compared a segment at a time.
 * Neither shortcut works: as strings "13.2" sorts before "2.10", and read as decimals 13.12 sorts
 * before 13.2 — yet lesson 13.12 comes after 13.2. Sub-lesson numbers do reach the teens in the
 * curriculum, so this is not a hypothetical.
 *
 * A segment that is not a number sorts as 0 rather than throwing: a malformed number should cost
 * that lesson its place in the order, not take the whole badge down with it.
 */
const segments = (lessonNumber: string): number[] => lessonNumber.split('.').map(part => Number(part) || 0);

export const byCurriculumSequence = (a: ReportLesson, b: ReportLesson): number => {
  const [left, right] = [segments(a.number), segments(b.number)];
  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    const difference = (left[i] ?? 0) - (right[i] ?? 0);
    if (difference !== 0) {
      return difference;
    }
  }
  return 0;
};

/**
 * Whether the student passed the exit ticket of the last lesson aligned to this skill (ON FIRE FR-2).
 *
 * "Last" is taken over *every* aligned lesson, including those carrying no exit ticket — the
 * requirement is written against the full set of aligned lessons. A skill whose final lesson has no
 * ticket therefore has nothing to pass and can never be on fire, which is a real population: roughly
 * a quarter of the skills in the curriculum are shaped that way.
 *
 * Read through lessonTicket, so a ticket passed on this device a moment ago counts straight away,
 * the same as it does for the badge's own count.
 */
const finalAlignedLessonPassed = (
  alignedLessons: readonly ReportLesson[],
  pendingFinish: PendingExitSlipFinish | null,
): boolean => {
  const finalLesson = [...alignedLessons].sort(byCurriculumSequence).pop();
  const ticket = finalLesson ? lessonTicket(finalLesson, pendingFinish) : null;
  return !!ticket && ticket.scoreRatio >= EXIT_SLIP_PASSING_RATIO;
};

/**
 * One row of the report — a skill and every curriculum lesson in the target group that infers it —
 * reduced to that skill's badge progress, or null when the skill earns no badge at all.
 *
 * Only lessons with `hasExitSlip` are related: the report also returns lessons that infer the skill
 * but carry no ticket, and counting those in the ON FIRE denominator would put part of the bar out
 * of the student's reach — a skill with one ticket across three lessons could never clear two thirds.
 */
export const toSkillExitSlipProgress = (
  row: SkillExitSlipReportRow,
  pendingFinish: PendingExitSlipFinish | null = null,
): SkillExitSlipProgress | null => {
  const relatedLessons = row.curriculumLessons.filter(lesson => lesson.hasExitSlip);

  const completedTickets = relatedLessons.flatMap(lesson =>
    lesson.classExitSlips.flatMap(assignment => {
      const ticket = assignmentTicket(lesson, assignment, pendingFinish);
      return ticket ? [ticket] : [];
    }),
  );

  // The badge is a read-out of what the student has done, so it needs at least one finished ticket
  // to say anything — passed or merely taken, either will do. Until then the card stays clean rather
  // than carrying a badge reading "0". This also covers a skill with no related lessons at all.
  if (completedTickets.length === 0) {
    return null;
  }

  return {
    skillId: toSkillNodeId(row.skillInfo.skillId),
    finalLessonPassed: finalAlignedLessonPassed(row.curriculumLessons, pendingFinish),
    completedTickets,
    relatedCount: relatedLessons.length,
  };
};

/**
 * The whole report as the badge slice wants it: one entry per skill that earns a badge, in the order
 * the report returned them. Rows that resolve to null (see above) are dropped rather than stored, so
 * a card with no exit tickets behind it renders no badge.
 *
 * `pendingFinish` is the ticket this device has seen finished but the server has not reported back.
 * Folding it in here is what gives a skill its very first badge the moment its first ticket is sat:
 * the row exists in the report all along, and only stops resolving to null once it has a ticket to
 * show.
 */
export const buildSkillExitSlipProgress = (
  report: ReportRows,
  pendingFinish: PendingExitSlipFinish | null = null,
): SkillExitSlipProgress[] =>
  report
    .map(row => toSkillExitSlipProgress(row, pendingFinish))
    .filter((progress): progress is SkillExitSlipProgress => progress !== null);

/**
 * Whether the student cleared this lesson's exit ticket, at the same two-thirds bar the rest of the
 * feature uses. A lesson never assigned to them, or assigned and not yet sat, both read as false.
 */
const isLessonPassed = (lesson: ReportLesson): boolean => {
  const session = latestAssignment(lesson)?.learnosityExitSlipSession;
  return !!session && sessionScoreRatio(session) >= EXIT_SLIP_PASSING_RATIO;
};

/**
 * The exit tickets that are the last thing standing between the student and a skill: every other
 * lesson that infers the skill has had its ticket passed, so clearing this one would get the skill
 * inferred via exit tickets.
 *
 * Today's banner reads this to know whether to promise "CHANCE TO EARN A SKILL!" — the caller matches
 * the ticket it is showing against the result (both in the "ClassExitSlipNode:<n>" form, which is why
 * the report's raw ints go through toClassExitSlipNodeId).
 *
 * The universe is the same one relatedCount counts: every lesson in the student's target group that
 * infers the skill and carries an exit ticket. A lesson whose ticket was never assigned to the
 * student counts as outstanding, and rightly holds the promise back — the skill cannot be inferred
 * while a ticket for it is still unanswered, whoever's fault that is. Which also means the one
 * lesson left has to have an assignment for there to be a ticket to make the promise on.
 *
 * Group-wide rather than unit-wide on purpose: the promise is about a skill, and a skill does not
 * stop being one ticket away because the student has walked to another unit.
 *
 * Once every ticket is passed, the passed ones are returned: a report fetched after the last pass
 * (a reload, a return to the dashboard) must still let the result card say the skill was earned.
 * The promise cannot pick them up — a passed ticket is finished, and only an unfinished one is offered.
 */
export const findSkillEarningExitSlipIds = (report: ReportRows): string[] =>
  report.flatMap(row => {
    const lessons = row.curriculumLessons.filter(lesson => lesson.hasExitSlip);
    const outstanding = lessons.filter(lesson => !isLessonPassed(lesson));
    if (outstanding.length > 1) {
      return [];
    }

    return (outstanding.length === 1 ? outstanding : lessons).flatMap(lesson => {
      const assignment = latestAssignment(lesson);
      return assignment ? [toClassExitSlipNodeId(assignment.classExitSlipId)] : [];
    });
  });
